import gsap, { TimelineLite } from 'gsap';

import { BaseBonusPadDrawer } from './BaseBonusPadDrawer';
import { gameConfig } from '../../../core/configs';
import {
  bonusPreBonusSharedRssNames,
  bonusSharedResources, crystalsRssNames,
  crystalTypes,
  movingLightEffectResourceNames,
} from '../../../core/gameConstants';
import { bonusPreBonusSharedDrawer } from '../../bonusPreBonusShared/BonusPreBonusSharedDrawer';
import {
  FREE_SPINS_STATE,
  MULTIPLIER_STATE,
  PRE_BONUS_CRYSTALS_STATE,
} from '../../../stateManagment/gameState';
import { bonusWheelDrawer } from '../../wheel/WheelDrawer';
import { spinResponseHelper } from '../../../services/helperService/SpinResponseHelper';
import { spinButtonHelper } from '../../../services/helperService/SpinButtonHelper';
import { soundService } from '../../../services/helperService/PixiSoundService';
import { calculateAngle } from '../../../core/helpers/calculateAngle';
import { delay } from '../../../core/helpers/delay';

export class BonusPadDesktopDrawer extends BaseBonusPadDrawer {
  constructor() {
    super();
    this.loader = this.createLoader();

    this.config = {
      ...this.config,
      margin: 14,
      positions: {
        content: {},
      },
      size: 270,
      spaceDistance: 260,
    };
  }

  async loadResource() {
    if (!this.resources) {
      const { res } = await super.loadResource(bonusSharedResources, this.loader);
      this.resources = res;
    }
    return this.resources;
  }

  async onLoad() {
    return this.loadResource();
  }

  setContainerPosition() {
    this.setPosition(this.container, gameConfig.wheelPosition);
  }

  setFreeSpin() {
    const { size, spaceDistance, margin } = this.config;

    const [freeSpin, onChange] = bonusPreBonusSharedDrawer.getFreeSpin({
      size,
    });
    const y = -size / 2 + margin;
    this.setPosition(freeSpin, { x: -spaceDistance - size, y });
    this.addSubscriber(FREE_SPINS_STATE.subscribe(onChange, true));
    this.container.addChild(freeSpin);
  }

  setMultiplier() {
    const { size, spaceDistance, margin } = this.config;
    const [multiplier, onChange] = bonusPreBonusSharedDrawer.getMultiplier({
      size: size,
    });
    const y = size / 2 - margin;

    this.setPosition(multiplier, { x: -spaceDistance - size, y });
    this.addSubscriber(MULTIPLIER_STATE.subscribe(onChange, true));
    this.container.addChild(multiplier);
  }

  setBonusRectangleSmallFirst() {
    const { size, spaceDistance } = this.config;

    const [bonusRectangleSmall1, onChange, containers] = bonusPreBonusSharedDrawer.getCrystalsContentX3_Big({
      size,
      crystals: [
        {
          name: bonusPreBonusSharedRssNames.crystalYellow,
          observerName: crystalTypes.crystal_yellow,
        },
        {
          name: bonusPreBonusSharedRssNames.crystalGreen,
          observerName: crystalTypes.crystal_green,
        },
        {
          name: bonusPreBonusSharedRssNames.crystalMagenta,
          observerName: crystalTypes.crystal_magenta,
        },
      ],
    });
    this.addSubscriber(PRE_BONUS_CRYSTALS_STATE.subscribe(onChange, true));
    this.setPosition(bonusRectangleSmall1, {
      x: spaceDistance + size,
    });
    this.calculateCrystalPositionByParent({
      crystal: containers[0],
      name: crystalTypes.crystal_yellow,
      parent: bonusRectangleSmall1,
    });
    this.calculateCrystalPositionByParent({
      crystal: containers[1],
      name: crystalTypes.crystal_green,
      parent: bonusRectangleSmall1,
    });
    this.calculateCrystalPositionByParent({
      crystal: containers[2],
      name: crystalTypes.crystal_magenta,
      parent: bonusRectangleSmall1,
    });
    this.container.addChild(bonusRectangleSmall1);
  }

  calculateCrystalPositionByParent({ crystal, name, parent }) {
    const { x, y } = crystal.position;
    const { x: cX, y: cY } = this.container.position;
    const { x: pX, y: pY } = parent.position;
    this.positions = {
      ...this.positions,
      [name]: { x: x + pX + cX, y: y + pY + cY },
    };
  }

  _createStar(duration = .2) {
    const star = this.createSprite(this.resources[movingLightEffectResourceNames.star]);
    star.anchor.set(.5);
    star.scale.set(.4);
    star.alpha = 0;

    const startAnimation = () => {
      new TimelineLite()
        .to(star, { duration, pixi: { scale: .5 }, alpha: .5 })
        .to(star, { duration, pixi: { scale: 1 }, alpha: .9 })
        .to(star, { duration, pixi: { scale: .5 }, alpha: .5 })
        .to(star, { duration, pixi: { scale: 1 }, alpha: .9 })
        .to(star, { duration, pixi: { scale: 0 }, alpha: 0 });
    };

    return [star, startAnimation];
  }

  _createLightEffectAnimation() {
    const texture = this.createSpine(this.resources[movingLightEffectResourceNames.lightEffectSpine]);
    texture.visible = false;
    const onStart = () => {
      texture.visible = true;
      texture.state.setAnimation(0, 'animation', false);
    };
    return [texture, onStart];
  }

  setWheelCrystals([wheel, container] = bonusWheelDrawer.containerAndWheel) {
    spinButtonHelper.disable();
    const {
      [crystalTypes.crystal_yellow]: yellowCount,
      [crystalTypes.crystal_green]: greenCount,
      [crystalTypes.crystal_magenta]: magentaCount,
    } = PRE_BONUS_CRYSTALS_STATE.getValue();
    const crystalPositions = wheel.children.map(({ position: { x, y } }) => ({ x, y }));
    const scale = .20;
    const duration = .5;
    let delayTime = -duration;
    let animationPromise = Promise.resolve();
    [
      {
        count: yellowCount,
        newType: crystalTypes.crystal_yellow,
        oldType: crystalTypes.crystal_green,
        image: crystalsRssNames.crystalYellow,
      },
      {
        count: greenCount,
        newType: crystalTypes.crystal_green,
        oldType: crystalTypes.crystal_magenta,
        image: crystalsRssNames.crystalGreen,
      },
      {
        count: magentaCount,
        newType: crystalTypes.crystal_magenta,
        oldType: crystalTypes.crystal_blue,
        image: crystalsRssNames.crystalMagenta,
      },
    ].forEach(({ count, oldType, newType, image }) => {
      animationPromise = new Promise(resolve => {
        if (count === 0) {
          resolve();
          return;
        }
        for (let i = 0; i < count; i++) {
          delayTime += duration;
          const crystal = this.createSprite(this.resources[image]);
          crystal.anchor.set(.5);
          crystal.scale.set(scale);
          const { x, y } = this.positions[newType];
          crystal.visible = false;

          const crystalContainer = this.createContainer();
          this.setPosition(crystalContainer, { x: x - container.position.x, y: y - container.position.y });

          const [star, startStarAnimation] = this._createStar();
          const lightContainer = this.createContainer();
          const [lightEffectAnimation, onStart] = this._createLightEffectAnimation();
          let index = spinResponseHelper.getRandomCrystal(oldType);

          const a = Math.abs(crystalPositions[index].x - (x - container.position.x));
          const b = Math.abs(crystalPositions[index].y - (y - container.position.y));
          const c = Math.sqrt(a ** 2 + b ** 2);

          const rect = this.createGraphics()
            .drawRect(
              0,
              0,
              c,
              20,
            );

          if (i === 0 && newType && newType === crystalTypes.crystal_magenta) {
            index = spinResponseHelper.getRandomCrystal(crystalTypes.crystal_gold);
            spinResponseHelper.setCrystalPlace({
              index,
              oldType: crystalTypes.crystal_gold,
              newType,
            });
          } else {
            spinResponseHelper.setCrystalPlace({
              index,
              oldType,
              newType,
            });
          }

          const angle = calculateAngle({
            x1: crystalPositions[index].x,
            x2: (x - container.position.x),
            y1: crystalPositions[index].y,
            y2: (y - container.position.y),
          });
          star.rotation = wheel.children[index].rotation;
          lightEffectAnimation.scale.set(c / lightEffectAnimation.width * 1.5);

          rect.angle = -(90 + angle);
          rect.pivot.x = lightEffectAnimation.width / 2;
          rect.addChild(lightEffectAnimation);
          lightContainer.addChild(rect);
          crystalContainer.addChild(
            lightContainer,
            star,
            crystal,
          );

          container.addChild(
            crystalContainer,
          );
          gsap.to(crystal,
            {
              delay: delayTime,
              duration,
              rotation: wheel.children[index].rotation,
              onComplete: () => {
                startStarAnimation();
              },
              pixi: {
                scale: .25,
              }
            });

          gsap.to(crystalContainer,
            {
              delay: delayTime,
              duration,
              x: crystalPositions[index].x,
              y: crystalPositions[index].y,
              onStart: () => {
                soundService.playElementMove();
                const res = PRE_BONUS_CRYSTALS_STATE.getValue();
                PRE_BONUS_CRYSTALS_STATE.next({
                  ...res,
                  [newType]: --res[newType],
                });
                crystal.visible = true;
                onStart();
              },
              onComplete: () => {
                wheel.children[index] = crystal;

                //check last animation
                if (i === count - 1) {
                  resolve();
                }
              },
            });
        }
      });
    });
    animationPromise.then(() => {
      if (yellowCount + greenCount + magentaCount) {
        delay(() => {
          bonusWheelDrawer.resetWheelCrystals(bonusWheelDrawer.containerAndWheel);
          spinButtonHelper.enable();
        }, 1000);
      }
    });
  }

  overrideStyles() {
    bonusPreBonusSharedDrawer.overrideStyles({
      titleStyle: {
        fontSize: 40,
      },
      multiplierStyle: {
        fontSize: 99.5,
      },
      freeSpinsStyle: {
        fontSize: 99.5,
      },
      crystalTextStyle: {
        fontSize: 46,
      },
    });
  }

  async executeBonusScene() {
    this.overrideStyles();
    await this.loadResource();
    await bonusPreBonusSharedDrawer.loadResource();
    this.setContainerPosition();
    this.setMultiplier();
    this.setFreeSpin();
    this.setBonusRectangleSmallFirst();
  }
}
